import { useCallback, useRef, useState } from 'react';
import { useGameStore, selectCanSubmitGuess, selectReserveHintsRemaining } from '../state/gameStore';
import type { GameAdapter } from '@crackpoint/shared/adapters';

interface UseGameLoopReturn {
  isLoading: boolean;
  canSubmitGuess: boolean;
  reserveHintsRemaining: number;

  startRun: () => Promise<void>;
  addDigit: (digit: number) => void;
  removeDigit: () => void;
  submitGuess: () => Promise<void>;
  drawReserveHint: () => Promise<void>;
  usePowerUp: (index: number) => Promise<void>;
  advanceToNextRoom: () => Promise<void>;
  returnToTitle: () => void;
}

export function useGameLoop(adapter: GameAdapter): UseGameLoopReturn {
  const [isLoading, setIsLoading] = useState(false);
  const adapterRef = useRef(adapter);
  adapterRef.current = adapter;

  const canSubmitGuess = useGameStore(selectCanSubmitGuess);
  const reserveHintsRemaining = useGameStore(selectReserveHintsRemaining);

  const setRun = useGameStore((s) => s.setRun);
  const setRoom = useGameStore((s) => s.setRoom);
  const setScreen = useGameStore((s) => s.setScreen);
  const addDigitAction = useGameStore((s) => s.addDigit);
  const removeDigitAction = useGameStore((s) => s.removeDigit);
  const recordGuess = useGameStore((s) => s.recordGuess);
  const addReserveHint = useGameStore((s) => s.addReserveHint);
  const addUsedPowerUp = useGameStore((s) => s.addUsedPowerUp);
  const setCompletionResult = useGameStore((s) => s.setCompletionResult);
  const resetRoomState = useGameStore((s) => s.resetRoomState);
  const resetGame = useGameStore((s) => s.resetGame);

  const startRun = useCallback(async () => {
    setIsLoading(true);
    try {
      const runState = await adapterRef.current.createRun();
      setRun(runState);

      const roomData = await adapterRef.current.generateRoom();
      setRoom(roomData);
      setScreen('room');
    } finally {
      setIsLoading(false);
    }
  }, [setRun, setRoom, setScreen]);

  const addDigit = useCallback(
    (digit: number) => {
      addDigitAction(digit);
    },
    [addDigitAction],
  );

  const removeDigit = useCallback(() => {
    removeDigitAction();
  }, [removeDigitAction]);

  const submitGuess = useCallback(async () => {
    const currentInput = useGameStore.getState().input;
    const currentRoom = useGameStore.getState().room;
    if (!currentRoom || currentInput.length !== currentRoom.code_length) return;

    setIsLoading(true);
    try {
      const guess = [...currentInput];
      const result = await adapterRef.current.submitGuess(guess);
      recordGuess(result, guess);

      if (result.solved) {
        const completion = await adapterRef.current.completeRoom();
        setCompletionResult(completion);
        setScreen('roomComplete');
      } else if (result.run_ended) {
        setScreen('gameOver');
      }
    } finally {
      setIsLoading(false);
    }
  }, [recordGuess, setCompletionResult, setScreen]);

  const drawReserveHint = useCallback(async () => {
    setIsLoading(true);
    try {
      const hint = await adapterRef.current.drawReserveHint();
      if (hint) {
        addReserveHint(hint);
      }
    } finally {
      setIsLoading(false);
    }
  }, [addReserveHint]);

  const usePowerUp = useCallback(
    async (index: number) => {
      setIsLoading(true);
      try {
        const powerUp = await adapterRef.current.usePowerUp(index);
        addUsedPowerUp(index, powerUp);
      } finally {
        setIsLoading(false);
      }
    },
    [addUsedPowerUp],
  );

  const advanceToNextRoom = useCallback(async () => {
    setIsLoading(true);
    try {
      resetRoomState();
      const roomData = await adapterRef.current.generateRoom();
      setRoom(roomData);
      setScreen('room');
    } finally {
      setIsLoading(false);
    }
  }, [resetRoomState, setRoom, setScreen]);

  const returnToTitle = useCallback(() => {
    resetGame();
    setScreen('title');
  }, [resetGame, setScreen]);

  return {
    isLoading,
    canSubmitGuess,
    reserveHintsRemaining,
    startRun,
    addDigit,
    removeDigit,
    submitGuess,
    drawReserveHint,
    usePowerUp,
    advanceToNextRoom,
    returnToTitle,
  };
}
